import React from "react";
import { cn } from "tailwind-variants";
import UseSvg from "common/vue/components/UseSvg.react.vue";
import PreCutPopup from "common/vue/components/PreCutPopup.react.vue";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { generateTimeRemainingText } from "common/utils/ui/generate-time-remaining";
import { trackRudderstackImageEdited } from "common/utils/rudderstack/tracker.react";
import { useI18n } from "intl/useI18n";
import { useDtfStore } from "./store";
import "./PreCut.scss";

export default function PreCut({ isModal = false, ...restProps }) {
  const { t, n } = useI18n();
  const store = useDtfStore();

  function preCutPrice() {
    return store.preCutPrice;
  }

  function preCut() {
    return store.preCut;
  }

  function preCutUpsellThreshold() {
    return store.preCutUpsellThreshold;
  }

  function enablePreCut() {
    return store.enablePreCut;
  }

  function experiments() {
    return store.experiments;
  }

  function totalQuantity() {
    return store.totalQuantity;
  }

  function preCutDuration() {
    return store.preCutDuration;
  }

  function productCartQuantity() {
    return store.productCartQuantity;
  }

  function dtfCategory() {
    return store.dtfCategory;
  }

  function showMultiSize() {
    return store.showMultiSize;
  }

  function isStandardArtwork() {
    return store.isStandardArtwork;
  }

  function artworks() {
    return store.artworks;
  }

  function isSpangle() {
    return store.isSpangle;
  }

  function sticker() {
    return store.sticker;
  }

  function businessLine() {
    return store.businessLine;
  }

  function isProColor() {
    return store.isProColor;
  }

  function totalSeconds() {
    // round up to nearest minute
    return Math.ceil((preCutDuration() * cartPlusQuantity()) / 60) * 60;
  }

  function cartPlusQuantity() {
    return productCartQuantity() + totalQuantity();
  }

  function showPreCutUpsell() {
    const showUpsell = Boolean(totalQuantity());
    if (preCutUpsellThreshold()) {
      const savedMinutes = totalSeconds() / 60;
      return savedMinutes >= preCutUpsellThreshold() && showUpsell;
    }
    return showUpsell;
  }

  function totalQuantityText() {
    return t("item", cartPlusQuantity());
  }

  function preCutSavedTimeText() {
    // convert to milliseconds first
    return generateTimeRemainingText(totalSeconds() * 1000 + Date.now());
  }

  function updatePreCut(...args) {
    return store.updatePreCut(...args);
  }

  function submitPreCutEvent() {
    const newState = preCut() ? "disable" : "enable";
    submitDtfEvent("pre_cut_toggle", {
      pre_cut: newState,
      is_upsell: true,
      page: "pdp"
    });
    const detectProductLine = obj => {
      if (obj.sticker) return "sticker";
      if (obj.isProColor) return "pro color";
      if (obj.isSpangle) return "spangle";
      return "dtf";
    };
    trackRudderstackImageEdited({
      product_line: detectProductLine({
        sticker: sticker(),
        isProColor: isProColor(),
        isSpangle: isSpangle()
      }),
      artwork: artworks()[0],
      edit_type: newState === "enable" ? "precut transfer selected" : "precut transfer removed",
      artworks_count: artworks().length,
      location: "pdp",
      pre_cut_transfers: preCut(),
      business_line: businessLine()
    });
  }

  function togglePreCut() {
    submitPreCutEvent();
    updatePreCut(!preCut());
  }

  return (
    <div
      {...restProps}
      className={cn("pre-cut scope-52b1b65c", restProps?.className, {
        "pre-cut--mau": isModal,
        "pre-cut--multi-size": showMultiSize()
      })}
    >
      {showPreCutUpsell() ? (
        <div className="pre-cut__upsell scope-52b1b65c">
          <span className="pre-cut__upsell-title scope-52b1b65c">Save {preCutSavedTimeText()}</span>
          <br className="scope-52b1b65c" /> By letting us precut your{" "}
          <span className="pre-cut__upsell-quantity scope-52b1b65c">{totalQuantityText()}</span>
          {}
          {productCartQuantity() ? (
            <span className="pre-cut__upsell-cart-quantity scope-52b1b65c">
              , including{" "}
              <span className="pre-cut__upsell-quantity scope-52b1b65c">
                {productCartQuantity()}
              </span>{" "}
              from your cart
            </span>
          ) : null}
          . {}
        </div>
      ) : (
        <div className="pre-cut__title scope-52b1b65c">Want to save time?</div>
      )}
      <div className="pre-cut__checkbox-wrapper scope-52b1b65c">
        <label
          className={cn("pre-cut__label scope-52b1b65c", {
            "pre-cut__label--standard-artwork": isStandardArtwork()
          })}
          data-testid="pre-cut-label"
        >
          <span className="scope-52b1b65c sr-only">Pre-cut</span>
          <input
            className="pre-cut__input js-pre-cut-checkbox scope-52b1b65c"
            type="checkbox"
            checked={preCut()}
            disabled={!enablePreCut()}
            onChange={togglePreCut}
            data-testid="pre-cut-input"
          />

          <span className="pre-cut__icon-wrapper scope-52b1b65c" />
          {preCut() ? (
            <UseSvg
              className="pre-cut__icon pre-cut__icon--full scope-52b1b65c"
              icon="check"
              data-testid="pre-cut-icon"
            />
          ) : null}
        </label>
        <div
          className={cn("scope-52b1b65c", {
            "pre-cut__text": true,
            "pre-cut__text--bold": preCut()
          })}
        >
          {" "}
          Pre-cut transfers ({n(preCutPrice(), "currency")} each){" "}
        </div>
        <PreCutPopup className="pre-cut__info scope-52b1b65c" data-testid="pre-cut-info" />
      </div>
      <div className="pre-cut__upsell-note scope-52b1b65c">
        {" "}
        This applies to all {dtfCategory()} orders{" "}
      </div>
    </div>
  );
}
